/**
 * Setup health copy and rules (DNKADM-0492 / DNKAPI-0714), shared by the Staff list
 * chip, the Staff detail card and the My schedule checklist. The server decides what is
 * missing; this file only says it in plain words.
 */
import type { StaffSetupHealth, StaffSetupItem, V2Staff } from '@/api/staff';

/** The two codes that do not stop a coach from being bookable. */
const WARNING_ITEMS: ReadonlySet<StaffSetupItem> = new Set([
  'CALENDAR_NOT_LINKED',
  'LOGIN_NOT_LINKED',
]);

export function isSetupWarning(item: StaffSetupItem): boolean {
  return WARNING_ITEMS.has(item);
}

/**
 * The list chip shows only for an active, live coach the server calls not bookable.
 * An inactive coach is never bookable, and its own Inactive chip already says so.
 */
export function showNotBookableChip(staff: V2Staff): boolean {
  return staff.isActive && staff.removedAt === undefined && staff.setupHealth?.bookable === false;
}

/** The plain-words line for each code on the Staff detail card. */
export const SETUP_ITEM_TEXT: Record<StaffSetupItem, string> = {
  NO_LOCATION: 'Not assigned to a location.',
  NO_HOURS: 'No weekly hours at any of their locations.',
  NO_SERVICES: 'Not on a published service offered where they have hours.',
  CALENDAR_NOT_LINKED: 'No booking calendar, so bookings don’t show on their calendar.',
  LOGIN_NOT_LINKED: 'No login linked, so they can’t sign in to see their own schedule.',
};

/** The fix-link label for each code on the Staff detail card. */
export const SETUP_ITEM_FIX: Record<StaffSetupItem, string> = {
  NO_LOCATION: 'Assign a location',
  NO_HOURS: 'Set hours',
  NO_SERVICES: 'Go to Services',
  CALENDAR_NOT_LINKED: 'Set booking calendar',
  LOGIN_NOT_LINKED: 'Link a login',
};

/**
 * The coach's weekly hours count as set when the server reports neither `NO_HOURS` nor
 * `NO_LOCATION` (hours are set per location, so no location means no hours). `null`
 * when there is no setup health to read.
 */
export function hoursAreSet(health: StaffSetupHealth | undefined): boolean | null {
  if (health === undefined) return null;
  return !health.missing.includes('NO_HOURS') && !health.missing.includes('NO_LOCATION');
}
